import { Check, X, Minus } from "lucide-react";
import { motion } from "framer-motion";

const comparisonData = [
  {
    feature: "Conexão",
    light: { value: "Imediata (QR)", status: "check" },
    elite: { value: "Oficial Meta", status: "check" },
    wati: { value: "Só API", status: "neutral" },
    respondio: { value: "Só API", status: "neutral" },
    aisensy: { value: "Só API", status: "neutral" },
  },
  {
    feature: "Custo/Mensagem",
    light: { value: "Zero", status: "check" },
    elite: { value: "Tabela Meta", status: "neutral" },
    wati: { value: "Cobrado", status: "x" },
    respondio: { value: "Cobrado", status: "x" },
    aisensy: { value: "Cobrado", status: "x" },
  },
  {
    feature: "Risco de Ban",
    light: { value: "Baixo", status: "neutral" },
    elite: { value: "Zero", status: "check" },
    wati: { value: "Baixo", status: "neutral" },
    respondio: { value: "Baixo", status: "neutral" },
    aisensy: { value: "Baixo", status: "neutral" },
  },
  {
    feature: "Atendentes",
    light: { value: "Ilimitados", status: "check" },
    elite: { value: "Ilimitados", status: "check" },
    wati: { value: "Por Seat", status: "x" },
    respondio: { value: "Por Seat", status: "x" },
    aisensy: { value: "Por Seat", status: "x" },
  },
  {
    feature: "Suporte BR",
    light: { value: "Sim", status: "check" },
    elite: { value: "Prioritário", status: "check" },
    wati: { value: "Inglês", status: "x" },
    respondio: { value: "Inglês", status: "x" },
    aisensy: { value: "Inglês", status: "x" },
  },
  {
    feature: "Preço/mês",
    light: { value: "R$ 397", isPrice: true },
    elite: { value: "R$ 497", isPrice: true },
    wati: { value: "~R$ 250", isPrice: true, status: "neutral" },
    respondio: { value: "~R$ 500", isPrice: true, status: "x" },
    aisensy: { value: "~R$ 600*", isPrice: true, status: "x" },
  },
];

const StatusIcon = ({ status }: { status?: string }) => {
  if (status === "check") {
    return (
      <div className="w-4 md:w-5 h-4 md:h-5 rounded-full bg-success-green/20 flex items-center justify-center">
        <Check size={10} className="md:w-3 md:h-3 text-success-green" />
      </div>
    );
  }
  if (status === "x") {
    return (
      <div className="w-4 md:w-5 h-4 md:h-5 rounded-full bg-destructive/20 flex items-center justify-center">
        <X size={10} className="md:w-3 md:h-3 text-destructive" />
      </div>
    );
  }
  return (
    <div className="w-4 md:w-5 h-4 md:h-5 rounded-full bg-muted flex items-center justify-center">
      <Minus size={10} className="md:w-3 md:h-3 text-muted-foreground" />
    </div>
  );
};

const ComparisonSection = () => {
  return (
    <section id="comparativo" className="py-16 md:py-24 relative" aria-labelledby="comparison-heading">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface-card/30 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-16"
        >
          <h2 id="comparison-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            Zavvi.ai vs.{" "}
            <span className="text-gradient-zavvi">Concorrência</span>
          </h2>
          <p className="text-base md:text-lg text-body-text px-4">
            Veja por que +500 empresas escolheram a Zavvi.
          </p>
          <p className="text-xs md:text-sm text-muted-foreground mt-2">
            * AiSensy: $99/mês (~R$ 600) + $20/mês por agente adicional
          </p>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-5xl mx-auto overflow-x-auto pb-4"
        >
          <div className="min-w-[600px]">
            {/* Table Header */}
            <div className="grid grid-cols-6 gap-1.5 md:gap-3 mb-3 md:mb-4">
              <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl">
                <span className="text-muted-foreground font-medium text-[10px] md:text-sm">
                  Recurso
                </span>
              </div>
              <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl neon-border-cyan text-center">
                <div className="font-display font-bold text-[10px] md:text-sm">
                  <span className="text-cyber-cyan">Light</span>
                </div>
              </div>
              <div className="relative glass-card p-2 md:p-4 pt-4 md:pt-6 rounded-lg md:rounded-xl neon-border-purple text-center overflow-visible">
                <div className="font-display font-bold text-[10px] md:text-sm">
                  <span className="text-electric-purple">Elite</span>
                </div>
              </div>
              <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl text-center">
                <div className="font-medium text-muted-foreground text-[10px] md:text-sm">
                  WATI
                </div>
              </div>
              <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl text-center">
                <div className="font-medium text-muted-foreground text-[10px] md:text-sm">
                  Respond.io
                </div>
              </div>
              <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl text-center">
                <div className="font-medium text-muted-foreground text-[10px] md:text-sm">
                  AiSensy
                </div>
              </div>
            </div>

            {/* Table Rows */}
            {comparisonData.map((row, index) => (
              <motion.div
                key={row.feature}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="grid grid-cols-6 gap-1.5 md:gap-3 mb-1.5 md:mb-2"
              >
                <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl flex items-center">
                  <span className="text-foreground font-medium text-[10px] md:text-sm">
                    {row.feature}
                  </span>
                </div>
                <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl flex items-center justify-center gap-0.5 md:gap-2">
                  {!row.light.isPrice && <StatusIcon status={row.light.status} />}
                  <span
                    className={`text-[10px] md:text-sm ${
                      row.light.isPrice ? "font-bold text-cyber-cyan" : "text-body-text"
                    }`}
                  >
                    {row.light.value}
                  </span>
                </div>
                <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl flex items-center justify-center gap-0.5 md:gap-2 bg-electric-purple/5">
                  {!row.elite.isPrice && <StatusIcon status={row.elite.status} />}
                  <span
                    className={`text-[10px] md:text-sm ${
                      row.elite.isPrice ? "font-bold text-electric-purple" : "text-body-text"
                    }`}
                  >
                    {row.elite.value}
                  </span>
                </div>
                <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl flex items-center justify-center gap-0.5 md:gap-2">
                  {!row.wati.isPrice && <StatusIcon status={row.wati.status} />}
                  <span
                    className={`text-[10px] md:text-sm ${
                      row.wati.isPrice ? "font-bold text-muted-foreground" : "text-body-text"
                    }`}
                  >
                    {row.wati.value}
                  </span>
                </div>
                <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl flex items-center justify-center gap-0.5 md:gap-2">
                  {!row.respondio.isPrice && <StatusIcon status={row.respondio.status} />}
                  <span
                    className={`text-[10px] md:text-sm ${
                      row.respondio.isPrice ? "font-bold text-muted-foreground" : "text-body-text"
                    }`}
                  >
                    {row.respondio.value}
                  </span>
                </div>
                <div className="glass-card p-2 md:p-4 rounded-lg md:rounded-xl flex items-center justify-center gap-0.5 md:gap-2">
                  {!row.aisensy.isPrice && <StatusIcon status={row.aisensy.status} />}
                  <span
                    className={`text-[10px] md:text-sm ${
                      row.aisensy.isPrice ? "font-bold text-muted-foreground" : "text-body-text"
                    }`}
                  >
                    {row.aisensy.value}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;
